import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';

@Component({
  selector: 'phoenix-json-highlight',
  template: `
    @for (chunk of renderedChunks; track $index) {
      <pre
        [innerHTML]="chunk"
        class="whitespace-pre-wrap break-words text-sm"
      ></pre>
    }
  `,
  standalone: false,
})
export class JsonHighlightComponent implements OnChanges {
  @Input() data!: object | string;
  renderedChunks: string[] = [];
  private readonly chunkSize = 10000;
  private fullJsonString = '';

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data']) {
      this.renderedChunks = [];
      this.fullJsonString = this.convertToJson(this.data);
      this.renderIncrementally();
    }
  }

  private convertToJson(data: object | string): string {
    try {
      return typeof data === 'string' ? data : JSON.stringify(data, null, 2);
    } catch (error) {
      return '{ "error": "Invalid JSON" }';
    }
  }

  private renderIncrementally(): void {
    let start = 0;

    const processChunk = () => {
      if (start < this.fullJsonString.length) {
        const end = this.chunkEnd(start);
        const chunk = this.fullJsonString.slice(start, end).replace(/\n$/, '');

        const highlightedChunk = this.highlightKeys(this.escapeHtml(chunk));
        this.renderedChunks.push(highlightedChunk);

        start = end;

        setTimeout(processChunk, 0);
      }
    };

    processChunk();
  }

  /**
   * Prefer to close a chunk on a line break to avoid splitting a key/value pair
   * across two chunks, which would break its highlighting.
   */
  private chunkEnd(start: number): number {
    const limit = start + this.chunkSize;

    if (limit >= this.fullJsonString.length) {
      return this.fullJsonString.length;
    }

    const lineBreak = this.fullJsonString.lastIndexOf('\n', limit);

    return lineBreak > start ? lineBreak + 1 : limit;
  }

  /**
   * Chunks are rendered through [innerHTML], so markup-looking characters
   * inside the JSON values (OCR text such as `<BackTo Address`) have to be
   * escaped, otherwise the browser parses them as tags and drops the content.
   */
  private escapeHtml(chunk: string): string {
    return chunk
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  /**
   * Only keys are highlighted: in a formatted JSON they always open a line, so
   * anchoring there keeps quoted colons inside a value from being matched.
   */
  private highlightKeys(chunk: string): string {
    return chunk.replace(
      /^(\s*)"((?:[^"\\]|\\.)*)"(?=\s*:)/gm,
      '$1"<span class="blue-theme-text-2">$2</span>"'
    );
  }
}
